import { useEffect, useState } from "react";
import {
  Card,
  Modal,
  Table,
  Typography,
  Tooltip,
  message,
  Input,
  List,
  Spin,
  Popover,
  Button,
  Row,
  Col,
  Tabs,
  Tag,
  Progress,
} from "antd";
import {
  RobotOutlined,
  InboxOutlined,
  FieldTimeOutlined,
  AlertOutlined,
  ExclamationCircleOutlined,
  DatabaseOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../configs";
import { getUser } from "../../utils/DataUser";
import GreetingCard from "../../components/GreetingCard";

const { Title, Text } = Typography;

// ====== Data RAK utama (O atas → A bawah) ======
const ROWS = [
  "O",
  "N",
  "M",
  "L",
  "K",
  "J",
  "I",
  "H",
  "G",
  "F",
  "E",
  "D",
  "C",
  "B",
  "A",
];

const makeRacks = () => {
  const data = [];
  for (const row of ROWS) {
    for (let i = 1; i <= 34; i++) {
      data.push({
        id: `${row}${i}`,
        row,
        col: i,
        type: "rack",
      });
    }
  }
  return data;
};

const RACKS = makeRacks();

// ====== CL (Cooler Lot) sejajar rak A–F ======
const CL_LOTS = Array.from({ length: 1 }, (_, i) => ({
  id: `CL.${i + 1}`,
  row: "CL",
  type: "cl",
}));

// ====== helper: build lot AG.1, AX.1, AY.1, AZ.1 dari A1 ======
const buildLotsForRack = (rowLetter, colNumber) => {
  const prefixes = ["G", "X", "Y", "Z"];
  return prefixes.map((p) => `${rowLetter}${p}.${colNumber}`);
};

/**
 * AI Summary Popover Component
 * Fetches and displays a summary for a given item.
 */
const AISummaryPopover = ({ item }) => {
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchSummary = async () => {
    if (summary) return; // already fetched
    setLoading(true);
    try {
      // Use the newly created AI route
      const res = await axiosInstance.post("/ai/summary", {
        type: item.type,
        id: item.id,
      });
      setSummary(res.data?.summary || "Tidak ada ringkasan.");
    } catch (err) {
      console.error(err);
      setSummary("Gagal memuat ringkasan AI.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover
      trigger="click"
      title={
        <span>
          <RobotOutlined style={{ marginRight: 5 }} /> Smart Resume
        </span>
      }
      content={
        <div style={{ width: 300, fontSize: 13 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              <Spin size="small" /> Menganalisa...
            </div>
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: summary
                  .replace(/\n/g, "<br/>")
                  .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>"),
              }}
            />
          )}
        </div>
      }
      onOpenChange={(visible) => {
        if (visible) fetchSummary();
      }}
    >
      <Button
        type="text"
        size="small"
        style={{
          color: "#1890ff",
          fontSize: 12,
          display: "flex",
          alignItems: "center",
        }}
        onClick={(e) => e.stopPropagation()} // Prevent triggering parent onClick
      >
        <RobotOutlined /> AI Summary
      </Button>
    </Popover>
  );
};

/**
 * Greeting Card Component
 */
/**
 * AI Summary Card Component (Auto Load)
 */
const AISummaryCard = ({ item }) => {
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.post("/ai/summary", {
          type: item.type,
          id: item.id,
        });
        setSummary(res.data?.summary || "Tidak ada ringkasan.");
      } catch (err) {
        console.error(err);
        setSummary("Gagal memuat ringkasan AI.");
      } finally {
        setLoading(false);
      }
    };
    if (item && item.id) fetchSummary();
  }, [item]);

  return (
    <div style={{ padding: 10 }}>
      {loading ? (
        <div style={{ textAlign: "center" }}>
          <Spin /> Menganalisa...
        </div>
      ) : (
        <div
          dangerouslySetInnerHTML={{
            __html: summary
              .replace(/\n/g, "<br/>")
              .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>"),
          }}
        />
      )}
    </div>
  );
};

export default function WarehouseLayout() {
  const history = useHistory();
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  // ====== MODAL STOCK DETAILS ======
  const [modalVisible, setModalVisible] = useState(false);
  // stockDetails & loadingStock are declared later as stockData/loadingStock.
  // We will rename usage in fetchStockDetails below to match.

  // Global Search State
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Debounce Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchText && searchText.length >= 2) {
        handleGlobalSearch(searchText);
      } else {
        setSearchResults([]);
      }
    }, 600);

    return () => clearTimeout(delayDebounceFn);
  }, [searchText]);

  const handleGlobalSearch = async (value) => {
    if (!value || value.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await axiosInstance.get("/global-search", {
        params: { q: value },
      });
      setSearchResults(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  // data incoming utk area loading
  const [incomingList, setIncomingList] = useState([]);
  const [loadingIncoming, setLoadingIncoming] = useState(false);

  // data outgoing utk area transit
  const [transitList, setTransitList] = useState([]);
  const [loadingTransit, setLoadingTransit] = useState(false);

  // data stok utk modal rak/CL
  const [stockData, setStockData] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [occupiedLots, setOccupiedLots] = useState({});

  // Dashboard Stats
  const [dashboardStats, setDashboardStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(false);

  // Generate QR LOT Gudang
  const [showLotQrModal, setShowLotQrModal] = useState(false);
  const [lotQrMode, setLotQrMode] = useState("all"); // "all" | "select"
  const [lotQrSelected, setLotQrSelected] = useState([]); // list of lot IDs
  const apiBase = process.env.REACT_APP_PDF_URL || "/api";

  // Build full lot list for selector (racks + CL)
  const ALL_LOTS = [...RACKS.map((r) => r.id), ...CL_LOTS.map((c) => c.id)];

  const fetchStockDetails = async () => {
    setLoadingStock(true);
    try {
      const response = await axiosInstance.get("/item-stock?limit=100");
      const items = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      setStockData(items); // Updated to match existing state
      setModalVisible(true);
    } catch (error) {
      console.error("Failed to fetch stock details", error);
      message.error("Gagal mengambil detail stok");
    } finally {
      setLoadingStock(false);
    }
  };

  const stockColumns = [
    { title: "Kode Barang", dataIndex: "item_code", key: "item_code" },
    { title: "Nama Barang", dataIndex: "item_name", key: "item_name" },
    {
      title: "Jumlah Kemasan",
      dataIndex: "jumlah_kemasan",
      key: "jumlah_kemasan",
    },
    {
      title: "Masa Timbun",
      dataIndex: "masa_timbun",
      key: "masa_timbun",
      render: (val) => `${val} Hari`,
    },
    { title: "Customer", dataIndex: "customer_name", key: "customer_name" },
  ];

  // tabel untuk item-stock
  const columnsItemStock = [
    { title: "SERI", dataIndex: "seri", key: "seri", width: 60 },
    { title: "NOPEN", dataIndex: "nopen", key: "nopen", width: 100 },
    { title: "KODE BARANG", dataIndex: "item_code", key: "item_code" },
    { title: "NAMA BARANG", dataIndex: "item_name", key: "item_name" },
    {
      title: "LOT",
      dataIndex: "lokasi_lot",
      key: "lokasi_lot",
      width: 90,
    },
    {
      title: "JUMLAH KEMASAN",
      dataIndex: "jumlah_kemasan",
      key: "jumlah_kemasan",
      width: 130,
      align: "right",
    },
    {
      title: "MASA TIMBUN",
      dataIndex: "masa_timbun",
      key: "masa_timbun",
      width: 110,
      align: "center",
      render: (val) => `${val || 0} Hari`,
    },
  ];

  // ====== FETCH FUNCTIONS ======
  const fetchIncoming = async () => {
    setLoadingIncoming(true);
    try {
      const user = getUser();
      const params = { stripping: 1 };

      // Filter di backend untuk user level 7 (Importir)
      if (
        user &&
        (user.id_level === 7 ||
          user.id_user_level === 7 ||
          user.id_level === "7" ||
          user.id_user_level === "7")
      ) {
        params.id_customer = user.username; // username = NPWP
      }

      const res = await axiosInstance.get("/incoming", { params });
      const list = res.data?.data || [];
      const filtered = list.filter(
        (inc) => inc.stripping === 0 || inc.stripping === 2,
      );
      setIncomingList(filtered);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingIncoming(false);
    }
  };

  const fetchTransit = async () => {
    setLoadingTransit(true);
    try {
      const user = getUser();
      const params = { status: "DRAFT", limit: 100 };

      // Filter di backend untuk user level 7 (Importir)
      if (
        user &&
        (user.id_level === 7 ||
          user.id_user_level === 7 ||
          user.id_level === "7" ||
          user.id_user_level === "7")
      ) {
        params.id_customer = user.username; // username = NPWP
      }

      const res = await axiosInstance.get("/outgoing", { params });
      const list = res.data?.data || [];
      setTransitList(list);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingTransitDetails(false);
      setLoadingTransit(false);
    }
  };

  const fetchOccupiedLots = async () => {
    try {
      const user = getUser();
      const params = {};

      // Filter di backend untuk user level 7 (Importir)
      if (
        user &&
        (user.id_level === 7 ||
          user.id_user_level === 7 ||
          user.id_level === "7" ||
          user.id_user_level === "7")
      ) {
        params.id_customer = user.username; // username = NPWP
      }

      const res = await axiosInstance.get("/item-stock/occupied-lots", {
        params,
      });
      // Convert to map: { 'LOT_ID': max_age }
      const map = {};
      const list = res.data || [];
      list.forEach((item) => {
        map[item.lokasi_lot] = item.max_age;
      });
      setOccupiedLots(map);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchItemStockByLots = async (lots = []) => {
    setLoadingStock(true);
    setStockData([]);
    try {
      const user = getUser();
      const params = {};

      // Filter di backend untuk user level 7 (Importir)
      if (
        user &&
        (user.id_level === 7 ||
          user.id_user_level === 7 ||
          user.id_level === "7" ||
          user.id_user_level === "7")
      ) {
        params.id_customer = user.username; // username = NPWP
      }

      const res = await axiosInstance.get("/item-stock/all-stock", { params });
      const allStock = res.data || [];
      const filtered = allStock.filter((item) =>
        lots.includes(item.lokasi_lot),
      );
      setStockData(filtered);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat stok barang");
    } finally {
      setLoadingStock(false);
    }
  };

  const fetchDashboardStats = async () => {
    setLoadingStats(true);
    try {
      const user = getUser();
      const params = {};

      // Filter di backend untuk user level 7 (Importir)
      if (
        user &&
        (user.id_level === 7 ||
          user.id_user_level === 7 ||
          user.id_level === "7" ||
          user.id_user_level === "7")
      ) {
        params.id_customer = user.username; // username = NPWP
      }

      const res = await axiosInstance.get("/item-stock/stats/summary", {
        params,
      });
      setDashboardStats(res.data || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStats(false);
    }
  };

  // INITIAL LOAD
  useEffect(() => {
    fetchIncoming();
    fetchTransit();
    fetchOccupiedLots();
    fetchDashboardStats();
    // eslint-disable-next-line
  }, []);

  // ====== CLICK HANDLERS ======
  const handleClickRack = (rack) => {
    const lots = buildLotsForRack(rack.row, rack.col);
    const combined = [rack.id, ...lots];
    setSelected({ id: rack.id, type: "rack", lots: combined });
    setOpen(true);
    fetchItemStockByLots(combined);
  };

  const handleClickCL = (cl) => {
    setSelected({ id: cl.id, type: "cl", lots: [cl.id] });
    setOpen(true);
    fetchItemStockByLots([cl.id]);
  };

  const handleClickIncoming = (inc) => {
    setSelected({ selectedType: "incoming", ...inc });
    setOpen(true);
    fetchIncomingDetails(inc.id_incoming);
  };

  const handleClickTransit = (out) => {
    setSelected({ selectedType: "transit", ...out });
    setOpen(true);
    fetchTransitDetails(out.id_outgoing);
  };

  // ====== NAVIGATION HANDLER ======
  const handleSearchResultClick = (item) => {
    if (item.type === "Incoming") {
      history.push(`/inventory/updateincoming/${item.id}`);
    } else if (item.type === "Outgoing") {
      history.push(`/inventory/updateoutgoing/${item.id}`);
    } else if (item.type === "Stripping") {
      history.push(`/inventory/updatetallysheet/${item.id}`);
    } else if (item.type === "Loading") {
      history.push(`/inventory/updatemuat/${item.id}`);
    } else if (item.type === "Stock") {
      history.push("/monitoring/stock");
      message.info(
        "Dialihkan ke Monitoring Stock. Silakan filter item code: " +
          item.title,
      );
    } else if (item.type === "Adjustment") {
      history.push("/other/adjustment");
      message.info(
        "Dialihkan ke Adjustment. Silakan cari nomor: " + item.title,
      );
    } else if (item.type === "StockOpname") {
      history.push("/other/opname");
      message.info(
        "Dialihkan ke Stock Opname. Silakan cari nomor: " + item.title,
      );
    } else if (item.type === "PindahLot") {
      history.push("/other/pindah-lot");
      message.info(
        "Dialihkan ke Pindah Lot. Silakan cari nomor: " + item.title,
      );
    } else if (item.type === "Pemusnahan") {
      history.push(`/inventory/updatepemusnahan/${item.id}`);
    } else if (item.type === "Item") {
      history.push("/referensi/komoditas");
      message.info(
        "Dialihkan ke Master Barang. Silakan cari barang: " + item.title,
      );
    } else if (item.type === "Company") {
      history.push(`/referensi/kontak/edit/${item.id}`);
      message.info("Membuka detail perusahaan: " + item.title);
    } else if (item.type === "User") {
      setSelected({ selectedType: "user_detail", ...item });
      setOpen(true);
    } else {
      message.info(`Topik: ${item.type} - ${item.title}`);
    }
  };

  // ====== STATE UNTUK DETAIL TRANSIT ======
  const [transitDetails, setTransitDetails] = useState([]);
  const [loadingTransitDetails, setLoadingTransitDetails] = useState(false);
  const [incomingDetails, setIncomingDetails] = useState([]);
  const [loadingIncomingDetails, setLoadingIncomingDetails] = useState(false);

  const fetchTransitDetails = async (idOutgoing) => {
    setLoadingTransitDetails(true);
    setTransitDetails([]);
    try {
      const res = await axiosInstance.get(
        `/outgoing/${idOutgoing}/with-details`,
      );
      let dets = res.data?.details || [];
      const user = getUser();
      if (user && user.id_level === 7) {
      }
      setTransitDetails(dets);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat detail barang transit");
    } finally {
      setLoadingTransitDetails(false);
    }
  };

  const fetchIncomingDetails = async (idIncoming) => {
    setLoadingIncomingDetails(true);
    setIncomingDetails([]);
    try {
      const res = await axiosInstance.get(
        `/incoming/${idIncoming}/with-details`,
      );
      let dets = res.data?.details || [];
      console.log("detls", dets);
      const user = getUser();
      if (user && user.id_level === 7) {
      }
      setIncomingDetails(dets);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat detail barang incoming");
    } finally {
      setLoadingIncomingDetails(false);
    }
  };

  // ====== DASHBOARD DETAILS STATE ======
  const [detailsList, setDetailsList] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [capacityData, setCapacityData] = useState({ used: [], empty: [] });

  const fetchCategoryDetails = async (category) => {
    setLoadingDetails(true);
    setDetailsList([]);
    try {
      const res = await axiosInstance.get("/item-stock/details", {
        params: { category },
      });
      setDetailsList(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat detail");
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchCapacityDetails = async () => {
    setLoadingDetails(true);
    setCapacityData({ used: [], empty: [] });
    try {
      const res = await axiosInstance.get("/item-stock/capacity-details");
      setCapacityData(res.data || { used: [], empty: [] });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat detail kapasitas");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCardClick = (type) => {
    if (type === "total") return;

    setSelected({ selectedType: "dashboard_stat", subtype: type });
    setOpen(true);

    if (type === "capacity") {
      fetchCapacityDetails();
    } else {
      fetchCategoryDetails(type);
    }
  };

  // ====== RENDER HELPERS ======
  const docDate = selected?.document_date
    ? dayjs(selected.document_date).format("DD MMMM YYYY")
    : "-";

  const renderModalHeader = () => {
    if (!selected) return null;
    if (
      selected.selectedType === "incoming" ||
      selected.selectedType === "transit"
    ) {
      const isTransit = selected.selectedType === "transit";
      return (
        <div
          style={{
            paddingBottom: 16,
            borderBottom: "1px solid #eee",
            marginBottom: 16,
          }}
        >
          {selected.selectedType === "incoming" && (
            <>
              <Text strong>Nomor BPB:&nbsp;</Text>
              <br />
              {selected.bpb_number || "-"}
              <br />
              <Text strong>Tanggal BPB:&nbsp;</Text>
              <br />
              {selected.bpb_date
                ? dayjs(selected.bpb_date).format("DD MMMM YYYY")
                : "-"}
              <br />
            </>
          )}

          <Text strong>
            {isTransit ? "Pemilik Barang:" : "Importir:"}&nbsp;
          </Text>
          <br />
          {selected.nama_pemilik || selected.nama_customer || "-"}
          <br />
          {!isTransit && (
            <>
              <Text strong>Pemasok:&nbsp;</Text>
              <br />
              {selected.id_contact || "-"}
              <br />
            </>
          )}

          <Text strong>Nomor Dokumen (NOPEN):&nbsp;</Text>
          <br />
          {selected.document_number || "-"}
          <br />
          <Text strong>Tanggal Dokumen:&nbsp;</Text>
          <br />
          {docDate}
          <br />
        </div>
      );
    }

    if (selected.selectedType === "user_detail") {
      return (
        <div style={{ marginBottom: 12 }}>
          <Text strong>REGISTER ID :&nbsp;&nbsp;&nbsp;</Text>
          {selected.id}
        </div>
      );
    }

    return (
      <div style={{ marginBottom: 12 }}>
        <Text strong>LOKASI RAK :&nbsp;&nbsp;&nbsp;</Text>
        {selected.id}
        <br />
        {Array.isArray(selected.lots) && selected.lots.length > 0 && (
          <Text type="secondary">LOT: {selected.lots.join(", ")}</Text>
        )}
      </div>
    );
  };

  const renderModalBody = () => {
    if (!selected) return null;

    if (selected.selectedType === "dashboard_stat") {
      if (selected.subtype === "capacity") {
        const columnsUsed = [
          { title: "Lot", dataIndex: "lot", key: "lot", width: 80 },
          {
            title: "Total Item",
            dataIndex: "total_items",
            key: "total_items",
            align: "right",
          },
          {
            title: "Total Qty",
            dataIndex: "total_qty",
            key: "total_qty",
            align: "right",
          },
        ];
        const columnsEmpty = [
          { title: "Lot", dataIndex: "lot", key: "lot" },
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: () => <Tag color="green">Empty</Tag>,
          },
        ];

        return (
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane
              tab={`Terpakai (${capacityData.used.length})`}
              key="1"
            >
              <Table
                dataSource={capacityData.used}
                columns={columnsUsed}
                loading={loadingDetails}
                pagination={{ pageSize: 10 }}
                size="small"
                rowKey="lot"
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab={`Kosong (${capacityData.empty.length})`} key="2">
              <Table
                dataSource={capacityData.empty}
                columns={columnsEmpty}
                loading={loadingDetails}
                pagination={{ pageSize: 20 }}
                size="small"
                rowKey="lot"
              />
            </Tabs.TabPane>
          </Tabs>
        );
      } else {
        // List Item (Slow, Attention, Critical)
        const columnsDetails = [
          { title: "Kode Barang", dataIndex: "item_code", key: "item_code" },
          { title: "Nama Barang", dataIndex: "item_name", key: "item_name" },
          { title: "Nopen", dataIndex: "nopen", key: "nopen" },
          {
            title: "Tgl Nopen",
            dataIndex: "tanggal_nopen",
            key: "tanggal_nopen",
            render: (d) => (d ? dayjs(d).format("DD MMM YYYY") : "-"),
          },
          {
            title: "Masa Timbun",
            dataIndex: "masa_timbun",
            key: "masa_timbun",
            render: (v) => `${v} Hari`,
          },
          { title: "Lot Asal", dataIndex: "lot_asal", key: "lot_asal" },
        ];

        return (
          <Table
            dataSource={detailsList}
            columns={columnsDetails}
            loading={loadingDetails}
            pagination={{ pageSize: 10 }}
            size="small"
            rowKey={(r) => r.item_code + r.nopen + r.lot_asal}
          />
        );
      }
    }

    if (selected.selectedType === "incoming") {
      // Incoming lists are simple
      if (selected.selectedType === "incoming") {
        const columnsIncoming = [
          { title: "Kode Barang", dataIndex: "item_code", key: "item_code" },
          { title: "Nama Barang", dataIndex: "item_name", key: "item_name" },
          {
            title: "Jumlah Kemasan",
            key: "kemasan",
            render: (_, r) => `${r.total_kemasan || 0} ${r.kemasan || ""}`,
          },
        ];

        return (
          <>
            <Title level={5} style={{ marginTop: 8 }}>
              <i> Detail Barang (Incoming)</i>
            </Title>
            <Table
              dataSource={incomingDetails}
              columns={columnsIncoming}
              pagination={false}
              bordered
              size="small"
              loading={loadingIncomingDetails}
              rowKey="id_incoming_detail"
            />
          </>
        );
      }
    }

    if (selected.selectedType === "transit") {
      const columnsTransit = [
        { title: "Kode Barang", dataIndex: "item_code", key: "item_code" },
        { title: "Nama Barang", dataIndex: "item_name", key: "item_name" },
        {
          title: "Jumlah Kemasan",
          key: "kemasan",
          render: (_, r) => `${r.total_kemasan || 0} ${r.kemasan || ""}`,
        },
        { title: "Lokasi Lot", dataIndex: "lot", key: "lot" },
      ];

      return (
        <>
          <Title level={5} style={{ marginTop: 8 }}>
            <i> Detail Barang (Transit)</i>
          </Title>
          <Table
            dataSource={transitDetails}
            columns={columnsTransit}
            pagination={false}
            bordered
            size="small"
            loading={loadingTransitDetails}
            rowKey="id_outgoing_detail"
          />
        </>
      );
    }

    // User Detail
    if (selected.selectedType === "user_detail") {
      return (
        <div style={{ padding: "20px 0", textAlign: "center" }}>
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "#e6f7ff",
                margin: "0 auto 10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: 32, color: "#1890ff" }}>
                {selected.title?.charAt(0)}
              </span>
            </div>
            <Title level={3}>{selected.title}</Title>
            <Text type="secondary">{selected.description}</Text>
          </div>

          <Card
            style={{
              marginTop: 20,
              textAlign: "left",
              border: "1px solid #f0f0f0",
            }}
            title="AI Analysis"
          >
            <div style={{ padding: 10 }}>
              <Text
                type="secondary"
                style={{
                  display: "block",
                  marginBottom: 10,
                  textAlign: "center",
                }}
              >
                Analisa kebiasaan dan aktivitas user menggunakan AI:
              </Text>
              <AISummaryCard item={{ type: "User", id: selected.id }} />
            </div>
          </Card>
        </div>
      );
    }

    return (
      <>
        <Title level={5} style={{ marginTop: 8 }}>
          <i> Detail Stok Barang</i>
        </Title>
        <Table
          dataSource={stockData}
          columns={columnsItemStock}
          pagination={false}
          bordered
          size="small"
          loading={loadingStock}
          scroll={{ x: 700 }}
          onRow={(record) => {
            let color = "inherit";
            let fontWeight = "normal";
            // Highlight text color based on age
            if (record.masa_timbun > 730) {
              color = "#ff4d4f";
              fontWeight = "bold";
            } // Critical
            else if (record.masa_timbun > 365) {
              color = "#fa8c16";
              fontWeight = "bold";
            } // Attention
            else if (record.masa_timbun > 180) {
              color = "#faad14";
              fontWeight = "bold";
            } // Slow Moving

            return { style: { color, fontWeight } };
          }}
        />
      </>
    );
  };

  const modalTitle = (() => {
    if (!selected) return "Detail";
    if (selected.selectedType === "incoming")
      return `Detail Incoming - ${selected.document_number || "-"}`;
    if (selected.selectedType === "transit")
      return `Detail Transit - ${selected.document_number || "-"}`;
    if (selected.selectedType === "dashboard_stat") {
      switch (selected.subtype) {
        case "slow_moving":
          return "Detail Slow Moving (6 bln - 1 thn)";
        case "attention":
          return "Detail Attention (1 - 2 thn)";
        case "critical":
          return "Detail Critical (> 2 thn)";
        case "capacity":
          return "Detail Kapasitas Gudang";
        default:
          return "Detail";
      }
    }
    if (selected.selectedType === "user_detail") return "User Profile";
    return `Detail Lokasi ${selected.id || "-"}`;
  })();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "#f9fafc",
        fontFamily: "Poppins, sans-serif",
        padding: "16px",
        paddingBottom: 80,
      }}
    >
      {/* ================== GREETING CARD ================== */}
      <GreetingCard />

      {/* ================== GLOBAL SEARCH ================== */}
      {/* ================== GLOBAL SEARCH ================== */}
      <div style={{ marginBottom: 16, position: "relative", zIndex: 50 }}>
        <Input.Search
          placeholder="Cari Dokumen, Barang, Rak, dll..."
          allowClear
          enterButton="Cari"
          size="large"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            if (!e.target.value) setSearchResults([]);
          }}
          onSearch={handleGlobalSearch}
        />

        {searchText && searchText.length >= 2 && (
          <div
            style={{
              marginTop: 4,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 4,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              maxHeight: 400,
              overflowY: "auto",
              padding: 8,
              zIndex: 9999, // Ensure this sits on top of everything
              position: "absolute",
              width: "100%",
              left: 0,
            }}
          >
            {searching ? (
              <div style={{ textAlign: "center", padding: 10 }}>
                <Spin size="small" /> Mencari...
              </div>
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={searchResults}
                renderItem={(item) => (
                  <List.Item
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #f0f0f0",
                      display: "flex",
                      alignItems: "center",
                    }}
                    actions={[<AISummaryPopover item={item} key="ai" />]}
                  >
                    <div
                      style={{ flex: 1, cursor: "pointer" }}
                      onClick={() => handleSearchResultClick(item)}
                    >
                      <List.Item.Meta
                        title={
                          <Text strong>
                            [{item.type}] {item.title}
                          </Text>
                        }
                        description={item.description}
                      />
                    </div>
                  </List.Item>
                )}
                locale={{
                  emptyText: (
                    <div style={{ padding: 16, textAlign: "center" }}>
                      <Text type="secondary" strong style={{ fontSize: 14 }}>
                        Maaf, data yang Anda cari tidak dapat ditemukan.
                      </Text>
                      <br />
                      <br />
                      <div
                        style={{
                          textAlign: "left",
                          display: "inline-block",
                          background: "#fafafa",
                          padding: 10,
                          borderRadius: 6,
                        }}
                      >
                        <Text strong>Tips:</Text>
                        <ul
                          style={{
                            paddingLeft: 20,
                            marginTop: 5,
                            marginBottom: 0,
                            color: "#666",
                          }}
                        >
                          <li>Cek ejaan kata kunci.</li>
                          <li>Coba cari Nomor Dokumen / Kode Barang.</li>
                        </ul>
                      </div>
                    </div>
                  ),
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* ================== STATS INFOGRAPHICS ================== */}
      {/* ================== STATS INFOGRAPHICS ================== */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* Card 1: Total Items */}
        <Col xs={24} sm={12} md={6}>
          <Card
            bodyStyle={{
              padding: "16px",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
            hoverable
            onClick={() => fetchStockDetails()}
          >
            <div
              style={{
                background: "#e6f7ff",
                padding: 12,
                borderRadius: 8,
                marginRight: 16,
              }}
            >
              <InboxOutlined style={{ fontSize: 24, color: "#1890ff" }} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Total Item Stok
              </Text>
              <Title level={4} style={{ margin: 0 }}>
                {loadingStats ? (
                  <Spin size="small" />
                ) : (
                  dashboardStats.totalItems || 0
                )}
              </Title>
            </div>
          </Card>
        </Col>

        {/* Card 2: Slow Moving */}
        <Col xs={24} sm={12} md={6}>
          <Card
            bodyStyle={{
              padding: "16px",
              display: "flex",
              alignItems: "center",
            }}
            hoverable
            onClick={() => handleCardClick("slow_moving")}
          >
            <div
              style={{
                background: "#fffbe6",
                padding: 12,
                borderRadius: 8,
                marginRight: 16,
              }}
            >
              <FieldTimeOutlined style={{ fontSize: 24, color: "#faad14" }} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Slow Moving (6-12 Bln)
              </Text>
              <Title level={4} style={{ margin: 0 }}>
                {loadingStats ? (
                  <Spin size="small" />
                ) : (
                  dashboardStats.slowMoving || 0
                )}
              </Title>
            </div>
          </Card>
        </Col>

        {/* Card 3: Attention */}
        <Col xs={24} sm={12} md={6}>
          <Card
            bodyStyle={{
              padding: "16px",
              display: "flex",
              alignItems: "center",
            }}
            hoverable
            onClick={() => handleCardClick("attention")}
          >
            <div
              style={{
                background: "#fff7e6",
                padding: 12,
                borderRadius: 8,
                marginRight: 16,
              }}
            >
              <ExclamationCircleOutlined
                style={{ fontSize: 24, color: "#fa8c16" }}
              />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Attention (1-2 Thn)
              </Text>
              <Title level={4} style={{ margin: 0 }}>
                {loadingStats ? (
                  <Spin size="small" />
                ) : (
                  dashboardStats.attention || 0
                )}
              </Title>
            </div>
          </Card>
        </Col>

        {/* Card 4: Critical */}
        <Col xs={24} sm={12} md={6}>
          <Card
            bodyStyle={{
              padding: "16px",
              display: "flex",
              alignItems: "center",
            }}
            hoverable
            onClick={() => handleCardClick("critical")}
          >
            <div
              style={{
                background: "#fff1f0",
                padding: 12,
                borderRadius: 8,
                marginRight: 16,
              }}
            >
              <AlertOutlined style={{ fontSize: 24, color: "#ff4d4f" }} />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Critical ({">"}2 Thn)
              </Text>
              <Title level={4} style={{ margin: 0 }}>
                {loadingStats ? (
                  <Spin size="small" />
                ) : (
                  dashboardStats.critical || 0
                )}
              </Title>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ================== QR LOT ACTION BUTTON ================== */}
      <div style={{ textAlign: "right", marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<QrcodeOutlined />}
          size="large"
          onClick={() => {
            setLotQrMode("all");
            setLotQrSelected([]);
            setShowLotQrModal(true);
          }}
          style={{
            background: "#52c41a",
            borderColor: "#52c41a",
            fontWeight: 700,
            borderRadius: 8,
            paddingLeft: 20,
            paddingRight: 20,
          }}
        >
          Generate QR LOT Gudang
        </Button>
      </div>

      {/* ================== MAIN CONTENT ================== */}
      <Card
        style={{ flex: 1, display: "flex", flexDirection: "column" }}
        bodyStyle={{ flex: 1, padding: 0 }}
      >
        <Row style={{ height: "100%", margin: 0 }}>
          {/* RAK (Left/Main) */}
          <Col
            xs={24}
            lg={14}
            xl={16}
            style={{ borderRight: "1px solid #f0f0f0", paddingBottom: 20 }}
          >
            <div style={{ padding: 12, overflowX: "auto" }}>
              <Title level={5} style={{ marginBottom: 6 }}>
                GUDANG ATLANTIK - POSISI RAK
              </Title>
              {ROWS.map((row) => {
                const racksPerRow = RACKS.filter((r) => r.row === row).sort(
                  (a, b) => b.col - a.col,
                );
                return (
                  <div
                    key={row}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 3,
                      minWidth: 700,
                    }}
                  >
                    {" "}
                    {/* Set minWidth to force scroll if needed */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(34, 1fr)",
                        gap: "2px",
                        flex: 1,
                      }}
                    >
                      {racksPerRow.map((rack) => {
                        const myLots = buildLotsForRack(rack.row, rack.col);
                        // Check occupancy and max age
                        let maxAge = -1;
                        let isOccupied = false;
                        const allLotsToCheck = [rack.id, ...myLots];

                        allLotsToCheck.forEach((lotId) => {
                          if (
                            Object.prototype.hasOwnProperty.call(
                              occupiedLots,
                              lotId,
                            )
                          ) {
                            isOccupied = true;
                            const age = occupiedLots[lotId];
                            if (age > maxAge) maxAge = age;
                          }
                        });

                        // Determine Color
                        let bgColor = "#f5f5f5";
                        if (isOccupied) {
                          if (maxAge > 730)
                            bgColor = "#ff4d4f"; // Critical
                          else if (maxAge > 365)
                            bgColor = "#fa8c16"; // Attention
                          else if (maxAge > 180)
                            bgColor = "#faad14"; // Slow Moving
                          else bgColor = "#52c41a"; // Normal
                        }

                        return (
                          <Tooltip key={rack.id} title={`Rak ${rack.id}`}>
                            <Card
                              hoverable
                              onClick={() => handleClickRack(rack)}
                              style={{
                                height: 24,
                                background: bgColor,
                                border: "1px solid #aaa",
                                textAlign: "center",
                                cursor: "pointer",
                                color: isOccupied ? "#fff" : "inherit",
                              }}
                              bodyStyle={{
                                padding: 0,
                                lineHeight: "24px",
                                fontSize: 10,
                              }}
                            >
                              {rack.col}
                            </Card>
                          </Tooltip>
                        );
                      })}
                    </div>
                    <div
                      style={{
                        width: 25,
                        marginLeft: 8,
                        textAlign: "center",
                        fontWeight: "bold",
                        color: "#444",
                      }}
                    >
                      {row}
                    </div>
                  </div>
                );
              })}
            </div>
          </Col>

          {/* TRANSIT & CL (Middle) */}
          <Col
            xs={24}
            md={12}
            lg={6}
            xl={4}
            style={{ borderRight: "1px solid #f0f0f0", background: "#fdfdfd" }}
          >
            <div
              style={{ padding: 12, display: "flex", flexDirection: "column" }}
            >
              {/* TRANSIT */}
              <div
                style={{
                  border: "2px solid #888",
                  borderRadius: 6,
                  padding: 8,
                  background:
                    "repeating-linear-gradient(-45deg,#f0f0f0,#f0f0f0 25px,#dcdcdc 25px,#dcdcdc 50px)",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 300,
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    marginBottom: 8,
                    color: "#444",
                    fontSize: 16,
                  }}
                >
                  AREA TRANSIT
                </div>
                <div style={{ flex: 1, paddingRight: 2 }}>
                  {!loadingTransit && transitList.length === 0 && (
                    <div style={{ textAlign: "center", padding: 10 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Kosong
                      </Text>
                    </div>
                  )}
                  {transitList.map((out) => (
                    <Card
                      key={out.id_outgoing}
                      hoverable
                      onClick={() => handleClickTransit(out)}
                      style={{
                        marginBottom: 6,
                        border: "1px solid #999",
                        background: "#fff",
                        cursor: "pointer",
                      }}
                      bodyStyle={{ padding: 6 }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ overflow: "hidden" }}>
                          <Text
                            strong
                            style={{ fontSize: 11, display: "block" }}
                          >
                            {out.document_number || "NOPEN -"}
                          </Text>
                          <Text
                            style={{
                              fontSize: 10,
                              display: "block",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                          >
                            {out.nama_pemilik || "-"}
                          </Text>
                        </div>
                        <div style={{ textAlign: "right", minWidth: 50 }}>
                          <Text
                            type="secondary"
                            style={{ fontSize: 9, display: "block" }}
                          >
                            {out.delivery_number || "-"}
                          </Text>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* CL */}
              <div
                style={{
                  border: "2px solid #888",
                  borderRadius: 6,
                  padding: 8,
                  background: "#f7fbff",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    marginBottom: 8,
                    color: "#444",
                  }}
                >
                  CL
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(1, 1fr)",
                    gap: 8,
                  }}
                >
                  {CL_LOTS.map((lot) => {
                    let isOccupied = false;
                    let maxAge = -1;
                    if (occupiedLots.hasOwnProperty(lot.id)) {
                      isOccupied = true;
                      maxAge = occupiedLots[lot.id];
                    }

                    let bgColor = "#e6f7ff";
                    if (isOccupied) {
                      if (maxAge > 730)
                        bgColor = "#ff4d4f"; // Critical
                      else if (maxAge > 365)
                        bgColor = "#fa8c16"; // Attention
                      else if (maxAge > 180)
                        bgColor = "#faad14"; // Slow Moving
                      else bgColor = "#52c41a"; // Normal
                    }

                    return (
                      <Card
                        key={lot.id}
                        hoverable
                        onClick={() => handleClickCL(lot)}
                        style={{
                          border: "2px solid #1890ff",
                          background: bgColor,
                          textAlign: "center",
                          cursor: "pointer",
                          color: isOccupied ? "#fff" : "inherit",
                        }}
                        bodyStyle={{
                          padding: 0,
                          lineHeight: "42px",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {lot.id}
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>
          </Col>

          {/* LOADING AREA (Right) */}
          <Col
            xs={24}
            md={12}
            lg={4}
            xl={4}
            style={{
              background:
                "repeating-linear-gradient(-45deg,#f0f0f0,#f0f0f0 25px,#dcdcdc 25px,#dcdcdc 50px)",
            }}
          >
            <div style={{ padding: 12 }}>
              {/* MOVED: CAPACITY CARD HERE */}
              <Card
                hoverable
                onClick={() => handleCardClick("capacity")}
                style={{
                  marginBottom: 16, // Spacing from Area Loading title
                  background:
                    "linear-gradient(to right bottom, #f6ffed, #ffffff)",
                  border: "1px solid #b7eb8f",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
                bodyStyle={{
                  padding: "10px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <DatabaseOutlined
                    style={{ color: "#52c41a", marginRight: 8, fontSize: 18 }}
                  />
                  <Text strong style={{ color: "#389e0d", fontSize: 13 }}>
                    Kapasitas Gudang
                  </Text>
                </div>

                <Progress
                  type="dashboard"
                  percent={dashboardStats.occupancyRate || 0}
                  width={80}
                  gapDegree={30}
                  strokeColor={{ "0%": "#87d068", "100%": "#green" }}
                  format={(percent) => (
                    <span
                      style={{
                        color: "#389e0d",
                        fontSize: 16,
                        fontWeight: "bold",
                      }}
                    >
                      {percent}%
                    </span>
                  )}
                />

                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    Terpakai / Total (Lot)
                  </Text>
                  <br />
                  {loadingStats ? (
                    <Spin size="small" />
                  ) : (
                    <Text strong style={{ fontSize: 14, color: "#237804" }}>
                      {dashboardStats.occupiedLots || 0}{" "}
                      <span style={{ fontSize: 10, color: "#8c8c8c" }}>
                        / {dashboardStats.totalLots || 0}
                      </span>
                    </Text>
                  )}
                </div>
              </Card>

              <Title
                level={3}
                style={{
                  color: "#444",
                  margin: 0,
                  marginBottom: 12,
                  textAlign: "center",
                }}
              >
                AREA LOADING
              </Title>
              <div>
                {!loadingIncoming && incomingList.length === 0 && (
                  <div style={{ textAlign: "center", marginTop: 20 }}>
                    <Text type="secondary">
                      Tidak ada barang incoming pending.
                    </Text>
                  </div>
                )}
                {incomingList.map((inc) => (
                  <Card
                    key={inc.id_incoming}
                    hoverable
                    onClick={() => handleClickIncoming(inc)}
                    style={{
                      marginBottom: 10,
                      border: "1px solid #aaa",
                      background: "#fff",
                    }}
                    bodyStyle={{ padding: 10 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div>
                        <Text strong style={{ fontSize: 13, display: "block" }}>
                          {inc.document_number || "Doc ?"}
                        </Text>
                        <Text style={{ fontSize: 11, color: "#666" }}>
                          {inc.nama_customer || "No Customer"}
                        </Text>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <Text
                          style={{
                            fontSize: 10,
                            display: "block",
                            color: "#888",
                          }}
                        >
                          {inc.bpb_number || "No BPB"}
                        </Text>
                        <Text
                          style={{
                            fontSize: 10,
                            display: "block",
                            color: "#888",
                          }}
                        >
                          {inc.container_number || "-"}
                        </Text>
                        <Text
                          style={{
                            fontSize: 10,
                            display: "block",
                            color: "#888",
                          }}
                        >
                          {dayjs(inc.document_date).format("DD/MM/YY")}
                        </Text>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* DETAIL MODAL */}
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        centered
        title={modalTitle}
        width="90%"
        style={{ maxWidth: 900 }}
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        {renderModalHeader()}
        {renderModalBody()}
      </Modal>

      {/* Spacer for Footer */}
      <div style={{ height: 60 }} />
      <Modal
        title="Detail Stok Barang"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Tutup
          </Button>,
        ]}
        width={800}
        centered
      >
        <Table
          columns={stockColumns}
          dataSource={stockData}
          loading={loadingStock}
          rowKey="id_item_stock"
          pagination={{ pageSize: 5 }}
          scroll={{ x: "max-content" }}
        />
      </Modal>

      {/* === Modal Generate QR LOT Gudang === */}
      <Modal
        title="Generate QR LOT Gudang"
        open={showLotQrModal}
        onCancel={() => setShowLotQrModal(false)}
        width={520}
        footer={null}
        bodyStyle={{ padding: 20 }}
      >
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
            {[
              {
                key: "all",
                icon: "🌐",
                label: "Semua LOT",
                sub: "Semua lot aktif di gudang",
              },
              {
                key: "select",
                icon: "🗂️",
                label: "Pilih LOT",
                sub: "Centang lot tertentu",
              },
            ].map((opt) => (
              <div
                key={opt.key}
                onClick={() => {
                  setLotQrMode(opt.key);
                  setLotQrSelected([]);
                }}
                style={{
                  flex: 1,
                  border: `2px solid ${lotQrMode === opt.key ? "#52c41a" : "#ddd"}`,
                  borderRadius: 8,
                  padding: "10px 14px",
                  cursor: "pointer",
                  textAlign: "center",
                  background: lotQrMode === opt.key ? "#f6ffed" : "#fff",
                  transition: "all .2s",
                }}
              >
                <div style={{ fontSize: 22 }}>{opt.icon}</div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</div>
                <div style={{ fontSize: 11, color: "#888" }}>{opt.sub}</div>
              </div>
            ))}
          </div>

          {lotQrMode === "select" &&
            (() => {
              const toggle = (id) =>
                setLotQrSelected((prev) =>
                  prev.includes(id)
                    ? prev.filter((x) => x !== id)
                    : [...prev, id],
                );
              const toggleRow = (rowId) => {
                const rowLots = RACKS.filter((r) => r.row === rowId).map(
                  (r) => r.id,
                );
                const allSelected = rowLots.every((id) =>
                  lotQrSelected.includes(id),
                );
                setLotQrSelected((prev) =>
                  allSelected
                    ? prev.filter((x) => !rowLots.includes(x))
                    : [...new Set([...prev, ...rowLots])],
                );
              };
              const chipStyle = (id) => ({
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 36,
                height: 28,
                padding: "0 8px",
                borderRadius: 6,
                background: lotQrSelected.includes(id) ? "#52c41a" : "#f5f5f5",
                color: lotQrSelected.includes(id) ? "#fff" : "#555",
                border: `1px solid ${lotQrSelected.includes(id) ? "#52c41a" : "#d9d9d9"}`,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 600,
                transition: "all .15s",
                userSelect: "none",
              });
              return (
                <div
                  style={{
                    border: "1px solid #e8e8e8",
                    borderRadius: 10,
                    padding: "14px 16px",
                    background: "#fafafa",
                    marginTop: 4,
                  }}
                >
                  {/* CL Section */}
                  <div style={{ marginBottom: 14 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#1890ff",
                        }}
                      >
                        🧊 Chiller (CL)
                      </span>
                      <span
                        onClick={() => {
                          const cl = CL_LOTS.map((c) => c.id);
                          const allSel = cl.every((id) =>
                            lotQrSelected.includes(id),
                          );
                          setLotQrSelected((prev) =>
                            allSel
                              ? prev.filter((x) => !cl.includes(x))
                              : [...new Set([...prev, ...cl])],
                          );
                        }}
                        style={{
                          fontSize: 11,
                          color: "#1890ff",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        Pilih Semua CL
                      </span>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {CL_LOTS.map((c) => (
                        <div
                          key={c.id}
                          style={chipStyle(c.id)}
                          onClick={() => toggle(c.id)}
                        >
                          {c.id}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Rack Section */}
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 13,
                        color: "#722ed1",
                        marginBottom: 8,
                      }}
                    >
                      🏗️ Rak Penyimpanan
                    </div>
                    <div
                      style={{
                        maxHeight: 240,
                        overflowY: "auto",
                        paddingRight: 4,
                      }}
                    >
                      {ROWS.map((row) => {
                        const rowLots = RACKS.filter((r) => r.row === row).sort(
                          (a, b) => a.col - b.col,
                        );
                        const allRowSel = rowLots.every((r) =>
                          lotQrSelected.includes(r.id),
                        );
                        return (
                          <div
                            key={row}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              marginBottom: 8,
                              padding: "6px 8px",
                              background: "#fff",
                              borderRadius: 8,
                              border: "1px solid #f0f0f0",
                            }}
                          >
                            <span
                              onClick={() => toggleRow(row)}
                              style={{
                                minWidth: 32,
                                height: 28,
                                borderRadius: 6,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: allRowSel ? "#722ed1" : "#f0e6ff",
                                color: allRowSel ? "#fff" : "#722ed1",
                                fontWeight: 700,
                                fontSize: 13,
                                cursor: "pointer",
                                border: "1px solid #d3adf7",
                                flexShrink: 0,
                              }}
                            >
                              {row}
                            </span>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 5,
                              }}
                            >
                              {rowLots.map((r) => (
                                <div
                                  key={r.id}
                                  style={chipStyle(r.id)}
                                  onClick={() => toggle(r.id)}
                                >
                                  {r.col}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}
        </div>

        {lotQrMode === "select" && (
          <div
            style={{
              fontSize: 12,
              color: lotQrSelected.length > 0 ? "#52c41a" : "#888",
              fontWeight: lotQrSelected.length > 0 ? 600 : 400,
              marginBottom: 10,
            }}
          >
            {lotQrSelected.length > 0
              ? `✅ ${lotQrSelected.length} lot dipilih`
              : "⬜ Belum ada lot yang dipilih"}
          </div>
        )}

        <button
          onClick={() => {
            if (lotQrMode === "select" && lotQrSelected.length === 0) {
              message.warning("Pilih minimal satu lot terlebih dahulu");
              return;
            }
            let lokasiParam;
            if (lotQrMode === "all") {
              lokasiParam = "ALL";
            } else {
              // Expand rack IDs (e.g. "O1") → main + sub-lots (OG.1, OX.1, OY.1, OZ.1)
              const expanded = new Set();
              lotQrSelected.forEach((id) => {
                expanded.add(id); // tambahkan ID asli (misal O1)
                const rackMatch = id.match(/^([A-Z])(\d+)$/); // hanya A1-Z34, bukan CL.x
                if (rackMatch) {
                  const [, row, col] = rackMatch;
                  buildLotsForRack(row, parseInt(col)).forEach((sub) =>
                    expanded.add(sub),
                  );
                }
              });
              lokasiParam = [...expanded].join(",");
            }
            window.open(
              `${apiBase}/item-stock/lot-qr-print?lokasi=${encodeURIComponent(lokasiParam)}`,
              "_blank",
            );
            setShowLotQrModal(false);
          }}
          style={{
            width: "100%",
            padding: "10px",
            background: "#52c41a",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: 15,
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          🖨 Generate &amp; Buka Halaman Cetak
        </button>
      </Modal>
    </div>
  );
}
